/**
 * Sorteggio aperture — la parte PURA, condivisa fra server e client.
 *
 * Nasce dentro `packages/server/src/game/opening-lottery.service.ts`, che resta il
 * chiamante storico (e conserva le pescate per TABELLA federale, usate solo da tornei
 * e bot). Qui vive solo ciò che serve anche al client: pescare per SOGLIA DI FORZA e
 * parsare la notazione. Il motivo dello spostamento è la Sfida AI, che sorteggia
 * l'apertura in locale senza passare da una room di gioco: duplicare la lotteria
 * avrebbe creato due sorgenti che derivano nel tempo.
 *
 * I dati sono in `../generated/openings.gen.js` (Regolamento Tecnico FID, estratto da
 * `pnpm codegen:openings`): 174 aperture italiane e 174 inglesi, con le tabelle
 * federali A/B/C/D. Le soglie di forza coincidono con le tabelle, ma si esprimono
 * meglio come tetto perché le due varianti annidano le tabelle in modo diverso.
 */

import { GameVariant } from './enums.js';
import { variantHasBallot } from './game-mode.js';
import type { BallotCeiling } from './game-mode.js';
import { ENGLISH_BALLOT, ITALIAN_BALLOT, forzaLevel, type BallotOpening } from '../generated/openings.gen.js';

/** Mossa parsata dalla notazione: caselle numerate, non coordinate. */
export interface ParsedMove {
  from: number;
  to: number;
}

/** Risultato del sorteggio, pronto da applicare a una scacchiera. */
export interface DrawnOpening {
  id: number;
  moves: string[];
  /** Codice FID di squilibrio, es. "PARI", "2N", "3B". Vuoto per le pescate casuali. */
  forza: string;
  parsedMoves: ParsedMove[];
  displayText: string;
}

/**
 * La parte dell'apertura sorteggiata che va a schermo.
 *
 * Un tipo solo per il payload sul filo (`game:start`, `rematch:start`,
 * `match:leg-start`) e per il campo `drawnOpening` degli store del client, che fino a
 * ieri ricopiavano lo stesso letterale in otto punti.
 */
export interface DrawnOpeningInfo {
  /** Numero di tabella federale; assente per le pescate senza tabella dietro. */
  id?: number;
  displayText?: string;
  moves?: string[];
  /**
   * Codice FID di squilibrio («PARI», «1N», «2B»…), quello che l'arbitro annuncia
   * accanto al numero. Assente per le pescate senza tabella dietro.
   */
  forza?: string;
}

/**
 * Il numero che l'arbitro annuncia — solo se la pescata viene DAVVERO da una riga di
 * tabella federale.
 *
 * `id` da solo non basta: `randomDrawnOpening` lo mette a 0, e il libro International
 * dei bot (`drawInternationalOpening`) ci mette l'INDICE della propria lista, 0-5.
 * Entrambi lasciano `forza` vuota, che è quello che li distingue da una riga di
 * tabella — senza questo filtro una partita International contro il bot annuncerebbe
 * «Apertura n. 3», un numero che non esiste.
 */
export function ballotNumber(opening: { id: number; forza: string }): number | undefined {
  return opening.forza !== '' && opening.id > 0 ? opening.id : undefined;
}

/** Confeziona un'apertura per l'annuncio a schermo: `id` solo se è un numero vero. */
export function drawnOpeningInfo(opening: DrawnOpening): DrawnOpeningInfo {
  const numero = ballotNumber(opening);
  return {
    ...(numero !== undefined ? { id: numero } : {}),
    displayText: opening.displayText,
    moves: opening.moves,
    ...(opening.forza ? { forza: opening.forza } : {}),
  };
}

/**
 * Parsa una mossa dalla notazione (es. "21-17" o "15x22").
 *
 * `maxSquare` è il numero di caselle giocabili della variante: 32 sulle 8x8 (il
 * default, che è anche l'unico spazio in cui vivono i ballot FID), 50 sull'International.
 */
export function parseMove(moveStr: string, maxSquare = 32): ParsedMove {
  // Sia "-" (mossa semplice) sia "x" (cattura) compaiono nella notazione delle tabelle.
  const separator = moveStr.includes('x') ? 'x' : '-';
  const parts = moveStr.split(separator);
  if (parts.length !== 2) {
    throw new Error(`Invalid move format: ${moveStr}`);
  }

  const from = parseInt(parts[0]!, 10);
  const to = parseInt(parts[1]!, 10);

  if (isNaN(from) || isNaN(to) || from < 1 || from > maxSquare || to < 1 || to > maxSquare) {
    throw new Error(`Invalid square numbers in move: ${moveStr}`);
  }

  return { from, to };
}

/** Parsa tutte le mosse di un'apertura. */
export function parseMoves(moves: readonly string[], maxSquare = 32): ParsedMove[] {
  return moves.map((m) => parseMove(m, maxSquare));
}

/** Confezione di una riga di ballot in un'apertura sorteggiata. */
export function toDrawnOpening(opening: BallotOpening, label: string): DrawnOpening {
  return {
    id: opening.id,
    moves: [...opening.moves],
    forza: opening.forza,
    parsedMoves: parseMoves(opening.moves),
    displayText: `${label} #${opening.id}: ${opening.moves.join(', ')} (${opening.forza})`,
  };
}

/**
 * Apertura sintetica per le pescate casuali (nessuna riga di tabella dietro).
 * Stessa forma di quella federale, così chi la riceve non deve distinguere i due casi.
 */
export function randomDrawnOpening(applied: readonly ParsedMove[]): DrawnOpening {
  return {
    id: 0,
    moves: applied.map((m) => `${m.from}-${m.to}`),
    forza: '',
    parsedMoves: [...applied],
    displayText: `Random (${applied.length})`,
  };
}

/** La variante ha un ballot inglese? Confronto insensibile al caso: la variante
 *  arriva come enum UPPERCASE dal server e come slug minuscolo dai dropdown web. */
function isEnglish(variant: GameVariant | string): boolean {
  return String(variant).toLowerCase() === String(GameVariant.ENGLISH).toLowerCase();
}

/** La tabella federale della variante: inglese o italiana, non ce ne sono altre. */
function tabellaDi(variant: GameVariant | string): readonly BallotOpening[] {
  return isEnglish(variant) ? ENGLISH_BALLOT : ITALIAN_BALLOT;
}

/**
 * Tutte le aperture della tabella di questa variante, o NESSUNA dove una tabella
 * non c'è — la lista da cui si sceglie.
 *
 * ⚠️ La guardia è la stessa di `trovaApertura` qui sotto, e le due devono
 * rispondere alla stessa domanda nello stesso modo: una tendina che offrisse
 * un'apertura che la ricerca per numero poi non ritrova sarebbe un elenco che
 * promette righe inesistenti. Senza, `tabellaDi` risponde «italiana» a tutto ciò
 * che non è inglese, e la scelta su un torneo internazionale si riempirebbe di
 * 174 aperture su caselle 1-32, che su una scacchiera da 50 non si giocano.
 */
export function tabellaAperture(variant: GameVariant | string): readonly BallotOpening[] {
  if (!variantHasBallot(String(variant))) return [];
  return tabellaDi(variant);
}

/**
 * L'apertura numero `id` della tabella di questa variante, o `null`.
 *
 * Il numero è quello che l'arbitro annuncia (1-174, `BallotOpening.id`), e le due
 * tabelle lo usano ognuna per conto proprio: il 42 italiano e il 42 inglese sono
 * due aperture diverse. Per questo la variante è il PRIMO argomento e non un
 * ripensamento — chiedere «l'apertura 42» senza dire di quale tabella non è una
 * domanda a cui si possa rispondere.
 *
 * Tollera un id assente o fuori tabella tornando `null`: il numero arriva dal
 * filo (il corpo di creazione di un torneo) e da una colonna che una riga vecchia
 * può non avere.
 */
export function trovaApertura(
  variant: GameVariant | string,
  id: number | null | undefined,
): BallotOpening | null {
  /*
   * ⚠️ Nessuna tabella, nessuna apertura — e la guardia sta QUI, non nei
   * chiamanti.
   *
   * `tabellaDi` risponde «italiana» a tutto cio' che non e' inglese, che e' la
   * risposta giusta per `ballotPool` (ci si arriva solo dopo un
   * `variantHasBallot` a monte) e sbagliatissima per una ricerca per numero,
   * che e' pubblica e prende la variante dal filo. Senza questa riga
   * «l'apertura 4» chiesta su un torneo internazionale tornava la 4 ITALIANA:
   * tre semi-mosse su caselle 1-32 posate su una scacchiera da 50, che
   * `applyForcedOpening` poi non riesce a giocare — cioe' un turno senza
   * apertura, e nessuno in grado di dire perche'.
   */
  if (!variantHasBallot(String(variant))) return null;
  if (typeof id !== 'number' || !Number.isInteger(id)) return null;
  return tabellaDi(variant).find((o) => o.id === id) ?? null;
}

/**
 * L'apertura SCELTA, nella forma che una stanza sa applicare — il gemello non
 * casuale di `drawBallotOpening`.
 *
 * Stessa etichetta della pescata (`Apertura`/`Opening`), quindi il `displayText`
 * che arriva al giocatore è identico: da dove l'apertura è uscita — un sorteggio o
 * la scelta di chi ha aperto il torneo — è una faccenda dell'organizzatore, e chi
 * si siede alla scacchiera legge lo stesso numero e le stesse tre semi-mosse.
 */
export function aperturaFissa(
  variant: GameVariant | string,
  id: number | null | undefined,
): DrawnOpening | null {
  const scelta = trovaApertura(variant, id);
  return scelta ? toDrawnOpening(scelta, isEnglish(variant) ? 'Opening' : 'Apertura') : null;
}

/** Aperture della variante entro la soglia di forza, dalla più equilibrata in su. */
export function ballotPool(variant: GameVariant | string, ceiling: BallotCeiling): readonly BallotOpening[] {
  return tabellaDi(variant).filter((o) => forzaLevel(o.forza) <= ceiling);
}

/**
 * Sorteggio per soglia di forza: nessuna apertura più squilibrata del tetto scelto.
 * Le cardinalità coincidono con le tabelle federali — italiana 83/126/166/174,
 * inglese 93/133/156/174.
 *
 * Puro: ritorna `null` sul pool vuoto invece di loggare (il wrapper server logga), e
 * accetta un `rng` iniettabile perché i test non debbano stubbare `Math.random`.
 */
export function drawBallotOpening(
  variant: GameVariant | string,
  ceiling: BallotCeiling,
  rng: () => number = Math.random,
): DrawnOpening | null {
  const pool = ballotPool(variant, ceiling);
  if (pool.length === 0) return null;
  const label = isEnglish(variant) ? 'Opening' : 'Apertura';
  return toDrawnOpening(pool[Math.floor(rng() * pool.length)]!, label);
}
